import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { reviewsAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { Search, Trash2, RefreshCw, Star, X } from 'lucide-react';

function StarRating({ rating, size }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          fill={i <= rating ? '#f59e0b' : 'none'}
          stroke={i <= rating ? '#f59e0b' : '#d1d5db'}
        />
      ))}
    </div>
  );
}

StarRating.propTypes = {
  rating: PropTypes.number.isRequired,
  size: PropTypes.number,
};

StarRating.defaultProps = {
  size: 13,
};

function DeleteModal({ review, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Delete Review</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: '8px 0 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Are you sure you want to delete the review by{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{review?.customerName}</strong>{' '}
            for <strong style={{ color: 'var(--text-primary)' }}>{review?.productName}</strong>?
            <br />
            <span style={{ color: 'var(--red)', fontSize: 12 }}>This action cannot be undone.</span>
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteModal.propTypes = {
  review: PropTypes.shape({
    customerName: PropTypes.string,
    productName: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

DeleteModal.defaultProps = {
  review: null,
};

function ReviewCard({ review, onDelete }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 14,
      padding: '14px 16px',
      boxShadow: 'var(--shadow-xs)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--primary-glow-sm)',
          color: 'var(--primary-dark)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, flexShrink: 0,
          border: '2px solid var(--primary-border)',
        }}>
          {review.customerName?.[0]?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {review.customerName || '—'}
            </span>
            <button
              className="btn btn-danger btn-icon btn-sm"
              style={{ flexShrink: 0, width: 30, height: 30, minHeight: 'unset', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => onDelete(review)}
            >
              <Trash2 size={13} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
            <StarRating rating={review.rating} size={12} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{review.rating}/5</span>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', flexShrink: 0 }}>
          Product
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {review.productName || '—'}
        </span>
      </div>
      {review.comment ? (
        <div style={{
          marginTop: 8,
          background: 'var(--bg-hover)',
          borderRadius: 8,
          padding: '8px 10px',
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          fontStyle: 'italic',
          borderLeft: '3px solid var(--primary-border)',
        }}>
          &ldquo;{review.comment}&rdquo;
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No comment
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
        {review.createdAt ? new Date(review.createdAt).toLocaleDateString('en-IN') : '—'}
      </div>
    </div>
  );
}

ReviewCard.propTypes = {
  review: PropTypes.shape({
    reviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customerName: PropTypes.string,
    productName: PropTypes.string,
    rating: PropTypes.number,
    comment: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function Reviews() {
  const [reviews, setReviews]             = useState([]);
  const [page, setPage]                   = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [ratingFilter, setRatingFilter]   = useState('ALL');
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [mobileItems, setMobileItems]     = useState([]);
  const [mobilePage, setMobilePage]       = useState(0);
  const [mobileHasMore, setMobileHasMore] = useState(true);
  const [mobileLoading, setMobileLoading] = useState(false);
  const sentinelRef = useRef(null);

  const toast = useToast();

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res  = await reviewsAPI.getAll(page, 20);
      const data = res.data;
      setReviews(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotal(data.totalElements || 0);
    } catch {
      toast('Failed to load reviews', 'error');
    } finally {
      setLoading(false);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const loadMobilePage = useCallback(async (pageNum, reset = false) => {
    if (mobileLoading) return;
    setMobileLoading(true);
    try {
      const res     = await reviewsAPI.getAll(pageNum, 15);
      const data    = res.data;
      const content = data.content || [];
      const tp      = data.totalPages || 0;
      setMobileItems(prev => reset ? content : [...prev, ...content]);
      setMobileHasMore(pageNum + 1 < tp);
      if (reset) setTotal(data.totalElements || 0);
    } catch {
      toast('Failed to load reviews', 'error');
    } finally {
      setMobileLoading(false);
    }
  }, [mobileLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setMobileItems([]);
    setMobilePage(0);
    setMobileHasMore(true);
    loadMobilePage(0, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && mobileHasMore && !mobileLoading) {
        const next = mobilePage + 1;
        setMobilePage(next);
        loadMobilePage(next);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [mobileHasMore, mobileLoading, mobilePage, loadMobilePage]);

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await reviewsAPI.delete(deleteTarget.reviewId);
      toast('Review deleted successfully', 'success');
      setReviews(prev => prev.filter(r => r.reviewId !== deleteTarget.reviewId));
      setMobileItems(prev => prev.filter(r => r.reviewId !== deleteTarget.reviewId));
      setTotal(t => t - 1);
      setDeleteTarget(null);
    } catch {
      toast('Failed to delete review', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const applyFilter = (list) => {
    const byRating = list.filter(r =>
      ratingFilter === 'ALL' || r.rating === Number(ratingFilter)
    );
    if (!search) return byRating;
    const query = search.toLowerCase();
    const words = query.split(/\s+/).filter(Boolean);
    return byRating.map(r => {
      const customer = (r.customerName || '').toLowerCase();
      const product  = (r.productName  || '').toLowerCase();
      const comment  = (r.comment      || '').toLowerCase();
      let sc = 0;
      if (customer === query)         sc += 1000;
      if (customer.startsWith(query)) sc += 500;
      if (product === query)          sc += 800;
      if (product.startsWith(query))  sc += 400;
      words.forEach(w => {
        if (customer.includes(w)) sc += 50;
        if (product.includes(w))  sc += 60;
        if (comment.includes(w))  sc += 20;
      });
      sc += (r.rating || 0) * 10;
      return { r, sc };
    }).filter(x => x.sc > 0)
      .sort((a, b) => b.sc - a.sc)
      .map(x => x.r);
  };

  const filtered       = applyFilter(reviews);
  const mobileFiltered = applyFilter(mobileItems);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : '—';

  return (
    <Layout title="Reviews" subtitle="View and manage customer product reviews">

      <div className="summary-grid" style={{ marginBottom: 18 }}>
        {[
          { value: total,                                     label: 'Total Reviews',   color: 'var(--text-primary)' },
          { value: <span style={{ display:'flex', alignItems:'center', gap:5 }}>{avgRating} <Star size={16} fill="#f59e0b" stroke="#f59e0b" /></span>, label: 'Avg Rating', color: '#f59e0b' },
          { value: reviews.filter(r => r.rating >= 4).length, label: 'Positive (4–5★)', color: 'var(--green)' },
          { value: reviews.filter(r => r.rating <= 2).length, label: 'Critical (1–2★)',  color: 'var(--red)'   },
        ].map((c) => (
          <div key={c.label} className="summary-card">
            <div className="summary-card-value" style={{ color: c.color }}>{c.value}</div>
            <div className="summary-card-label">{c.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <div className="search-box" style={{ flex: 1 }}>
            <Search size={14} className="search-icon" />
            <input
              placeholder="Search by customer, product, or comment..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => load()} style={{ flexShrink: 0 }}>
            <RefreshCw size={13} />
          </button>
        </div>
        <div style={{
          display: 'flex', gap: 6, alignItems: 'center',
          overflowX: 'auto', scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch', paddingBottom: 2,
        }}>
          {['ALL', '5', '4', '3', '2', '1'].map(s => (
            <button
              key={s}
              className={`chip ${ratingFilter === s ? 'active' : ''}`}
              style={{ flexShrink: 0, margin: 0 }}
              onClick={() => setRatingFilter(s)}
            >
              {s === 'ALL' ? 'All Stars' : `${s} ★`}
            </button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, paddingLeft: 4, whiteSpace: 'nowrap' }}>
            {filtered.length} review{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="reviews-mobile-list">
        {mobileLoading && mobilePage === 0 ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : mobileFiltered.length === 0 && !mobileLoading ? (
          <div className="empty-state">
            <Star size={36} style={{ color: 'var(--text-muted)', marginBottom: 10 }} />
            <h3>No reviews found</h3>
            <p>{search ? 'Try a different search.' : 'No reviews yet.'}</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {mobileFiltered.map(review => (
                <ReviewCard key={review.reviewId} review={review} onDelete={setDeleteTarget} />
              ))}
            </div>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {mobileLoading && mobilePage > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                <div className="spinner" style={{ width: 24, height: 24 }} />
              </div>
            )}
            {!mobileHasMore && mobileFiltered.length > 0 && (
              <div style={{ textAlign: 'center', padding: '14px 0 4px', fontSize: 12, color: 'var(--text-muted)' }}>
                All reviews are loaded ({mobileFiltered.length})
              </div>
            )}
          </>
        )}
      </div>

      <div className="reviews-desktop-table card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Customer</th>
                <th>Rating</th>
                <th>Comment</th>
                <th>Date</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>
                    <div className="spinner" style={{ margin: 'auto' }} />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: '40px 0' }}>
                      <Star size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                      <h3>No reviews found</h3>
                      <p>{search ? 'Try a different search term.' : 'No reviews yet.'}</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map((review, idx) => (
                <tr key={review.reviewId}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{page * 20 + idx + 1}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>
                      {review.productName || '—'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: 'var(--primary-glow-sm)', color: 'var(--primary-dark)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {review.customerName?.[0]?.toUpperCase() || '?'}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{review.customerName || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <StarRating rating={review.rating} />
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{review.rating}/5</span>
                    </div>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    {review.comment ? (
                      <span title={review.comment} style={{
                        display: 'block', fontSize: 13, color: 'var(--text-secondary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250,
                      }}>
                        &ldquo;{review.comment}&rdquo;
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No comment</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {review.createdAt ? new Date(review.createdAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="btn btn-danger btn-icon btn-sm" onClick={() => setDeleteTarget(review)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>Showing {filtered.length} of {total} reviews</span>
          <div className="pagination-controls">
            <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {page + 1} of {Math.max(1, totalPages)}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <DeleteModal
          review={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}
    </Layout>
  );
}
